import { Chess, type Color, type PieceSymbol, type Square } from "chess.js";

export type JourneyPiece = { id: Square; square: Square; type: PieceSymbol; color: Color; index: number };
export type JourneyMove = { id: Square; from: Square; to: Square; san: string; start: number; end: number };
export type PiecePose = { x: number; y: number; z: number; rx: number; ry: number; rz: number; air: number; scale: number };

export const clamp = (n: number, low = 0, high = 1) => Math.min(high, Math.max(low, n));
export const mix = (a: number, b: number, t: number) => a + (b - a) * t;
export const smooth = (t: number) => { const n = clamp(t); return n * n * (3 - 2 * n); };
export const interval = (p: number, start: number, end: number) => smooth((p - start) / (end - start));
export const squarePosition = (square: Square) => ({ x: square.charCodeAt(0) - 100.5, z: 4.5 - Number(square[1]) });
export const scrollProgress = (top: number, height: number, viewport: number, header: number) => clamp((header - top) / Math.max(1, height - viewport));
export const chapterAt = (p: number) => p < .21 ? 0 : p < .435 ? 1 : p < .82 ? 2 : 3;

const game = new Chess();
export const JOURNEY_PIECES: JourneyPiece[] = game.board().flatMap((rank) => rank.filter((p) => p !== null)).map((p, index) => ({ ...p, id: p.square, index }));
const identities = new Map<Square, Square>(JOURNEY_PIECES.map((p) => [p.square, p.id]));
export const JOURNEY_MOVES: JourneyMove[] = ["e4", "e5", "Nf3", "Nc6", "Bc4", "Bc5", "c3", "Nf6"].map((san, i) => {
  const move = game.move(san);
  const id = identities.get(move.from)!;
  identities.delete(move.from); identities.set(move.to, id);
  return { id, from: move.from, to: move.to, san: move.san, start: .445 + i * .044, end: .478 + i * .044 };
});
export const JOURNEY_FINAL_FEN = game.fen();

// Five foreground sculptures lead the opening composition. Every piece returns
// to its real size and square before the playable opening begins.
const FOREGROUND: Partial<Record<Square, { x: number; y: number; z: number; scale: number }>> = {
  g1: { x: 1.2, y: 3.3, z: 2, scale: 1.8 },
  e1: { x: -2.4, y: 2.9, z: .8, scale: 1.25 },
  h8: { x: 3.7, y: 3.7, z: -2, scale: 1.3 },
  a2: { x: -4.2, y: 2.2, z: 1, scale: 1.25 },
  f8: { x: -1.4, y: 4, z: -2, scale: 1.2 },
};

/** Pure, reversible choreography: a piece's pose depends only on scroll progress. */
export function piecePose(piece: JourneyPiece, progress: number): PiecePose {
  const p = clamp(progress);
  const { x, z } = squarePosition(piece.square);
  const seed = piece.index * 2.399963;
  const settle = interval(p, .035 + (piece.index % 8) * .009, .29 + (piece.index % 7) * .017);
  const air = 1 - settle;
  const curl = Math.sin(settle * Math.PI);
  const spread = piece.type === "p" ? 1.15 : 1.4;
  const foreground = FOREGROUND[piece.id];
  const pose: PiecePose = {
    x: mix(foreground?.x ?? x * spread + Math.sin(seed) * 1.8, x, settle) + Math.cos(seed) * curl * 1.15,
    y: (foreground?.y ?? 1.4 + ((piece.index * 7) % 11) * .16) * air + curl * 1.4,
    z: mix(foreground?.z ?? z * .9 + Math.cos(seed) * 1.6, z, settle) + Math.sin(seed) * curl * 1.15,
    rx: Math.sin(seed) * .48 * air,
    ry: (piece.color === "b" ? Math.PI : 0) + (piece.id === "g1" ? .25 : Math.cos(seed) * .9) * air + curl * .5,
    rz: Math.cos(seed) * .42 * air,
    air,
    scale: mix(foreground?.scale ?? 1, 1, settle),
  };
  for (const move of JOURNEY_MOVES) {
    if (move.id !== piece.id || p <= move.start) continue;
    const t = interval(p, move.start, move.end);
    const a = squarePosition(move.from), b = squarePosition(move.to);
    pose.x = mix(a.x, b.x, t); pose.z = mix(a.z, b.z, t);
    pose.y = Math.sin(t * Math.PI) * (piece.type === "n" ? 1.3 : .32);
    pose.rz = Math.sin(t * Math.PI * 2) * (piece.type === "n" ? .13 : .035);
  }
  return pose;
}

type CameraKey = { p: number; position: [number, number, number]; target: [number, number, number]; turn: number };
export const CAMERA_KEYS: CameraKey[] = [
  { p: 0, position: [10, 10.5, 15.5], target: [0, 1.1, 0], turn: -.18 },
  { p: .2, position: [8, 14, 15], target: [0, 1, 0], turn: 0 },
  { p: .42, position: [-2.5, 16, 13], target: [0, 0, 0], turn: .1 },
  { p: .59, position: [-7.5, 12, 13], target: [0, .2, 0], turn: .05 },
  { p: .8, position: [2, 11.5, 15], target: [0, .2, 0], turn: -.1 },
  { p: 1, position: [10, 10.5, 13.5], target: [0, .2, 0], turn: -.18 },
];

export function cameraPose(progress: number) {
  const p = clamp(progress);
  const index = Math.max(0, CAMERA_KEYS.findIndex((key) => key.p >= p) - 1);
  const a = CAMERA_KEYS[index], b = CAMERA_KEYS[index + 1];
  const t = interval(p, a.p, b.p);
  return {
    position: a.position.map((v, i) => mix(v, b.position[i], t)) as [number, number, number],
    target: a.target.map((v, i) => mix(v, b.target[i], t)) as [number, number, number],
    turn: mix(a.turn, b.turn, t),
  };
}

export function currentMove(progress: number) {
  return JOURNEY_MOVES.findLastIndex((move) => progress >= move.start);
}
